import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  useHydrated,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { Toaster } from "@/components/ui/sonner";

import appCss from "../styles.css?url";
import bebasNeueWoff2 from "@fontsource/bebas-neue/files/bebas-neue-latin-400-normal.woff2?url";
import sora400Woff2 from "@fontsource/sora/files/sora-latin-400-normal.woff2?url";
import manrope400Woff2 from "@fontsource/manrope/files/manrope-latin-400-normal.woff2?url";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { m } from "@/paraglide/messages.js";
import { getLocale, getTextDirection } from "@/paraglide/runtime";
import { localizedPath } from "@/lib/seo";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold">{m.error_404_title()}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{m.error_404_body()}</p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {m.error_404_cta()}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">{m.error_page_title()}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{m.error_page_body()}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {m.error_try_again()}
          </button>
          <a
            href={localizedPath("/", getLocale())}
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            {m.error_go_home()}
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: m.meta_default_title() },
      {
        name: "description",
        content: m.meta_default_description(),
      },
      { name: "author", content: "Mark Egly Golf" },
      { property: "og:site_name", content: "Mark Egly Golf" },
      { property: "og:title", content: m.meta_og_title() },
      {
        property: "og:description",
        content: m.meta_og_description(),
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      ...(appCss ? [{ rel: "stylesheet", href: appCss }] : []),
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "apple-touch-icon", href: "/favicon.svg" },
      {
        rel: "preload",
        href: bebasNeueWoff2,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: sora400Woff2,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: manrope400Woff2,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  // Vitest renders into a div; skip the document shell to avoid invalid DOM nesting.
  if (import.meta.env.VITEST) {
    return <>{children}</>;
  }

  return (
    <html lang={getLocale()} dir={getTextDirection()} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function AuthListener() {
  const router = useRouter();
  const queryClient = useQueryClient();
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => subscription.unsubscribe();
  }, [router, queryClient]);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isAdminRoute = pathname.startsWith("/admin");
  const isEmployeeRoute = pathname.startsWith("/employee");
  const isTvRoute = pathname === "/tv" || pathname === "/tv/";
  // Defer admin/employee chrome hiding until after hydration. Client redirects
  // (ssr:false authenticated routes) can change pathname before React hydrates,
  // which would otherwise omit SiteHeader on one side and include it on the other.
  const chromeReady = useHydrated();
  const hideSiteChrome = isTvRoute || (chromeReady && (isAdminRoute || isEmployeeRoute));

  if (isTvRoute) {
    return (
      <QueryClientProvider client={queryClient}>
        <AuthListener />
        <Outlet />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthListener />
      <div className="flex min-h-screen flex-col">
        {!hideSiteChrome && <SiteHeader />}
        <main className="flex-1">
          <Outlet />
        </main>
        {!hideSiteChrome && <SiteFooter />}
      </div>
      <Toaster richColors position="top-center" />
    </QueryClientProvider>
  );
}
